import type { User } from '../../types/User';

/**
 * Perfis do Cognito (claim `cognito:groups`).
 *
 * Antes do AF-3649 estas strings viviam como `const` dentro da função AppRoutes, sem export,
 * então qualquer tela que precisasse checar perfil tinha que redeclarar a string. Centralizar
 * aqui é o que permite gatear ações dentro das páginas, e não só o acesso à rota.
 */
export const ROLES = {
    ADMIN: 'ADMIN',
    OPERATOR: 'OPERATOR',
    LEGAL: 'LEGAL',
    CREDIT_ANALYST: 'CREDIT_ANALYST',
    CREDIT_MANAGER: 'CREDIT_MANAGER',
    FINANCIAL: 'FINANCIAL',
    CONTRACT_OPERATIONS_ANALYST: 'CONTRACT_OPERATIONS_ANALYST',
    INDIVIDUAL_PERSON: 'INDIVIDUAL_PERSON',
    COLLECTION_ANALYST: 'COLLECTION_ANALYST',
    BLOCKLIST: 'BLOCKLIST',
    MLP_ANALYST: 'MLP_ANALYST',
    COMMERCIAL_MANAGER: 'COMMERCIAL_MANAGER',
} as const;

export type Role = typeof ROLES[keyof typeof ROLES];

/** Se o usuário possui ao menos um dos perfis exigidos. */
export const hasAnyRole = (user: User | null | undefined, roles: readonly string[]): boolean => {
    if (!user || roles.length === 0) return false;
    return user.roles.some(role => roles.includes(role));
};

/**
 * Quem pode enviar um contrato para assinatura (AF-3649).
 * Decisão de negócio: apenas o jurídico envia. ADMIN mantém o acesso, OPERATOR não envia.
 */
export const canSendContractToSignature = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [ROLES.ADMIN, ROLES.LEGAL]);
};

/**
 * Quem pode criar e editar operações.
 * LEGAL acessa a lista de contratos apenas para leitura — as rotas `operations/*` não
 * liberam LEGAL, então as ações que navegam para lá ficam escondidas para esse perfil.
 */
export const canManageOperations = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [ROLES.ADMIN, ROLES.OPERATOR]);
};

/**
 * Quem acessa os Tipos de Contrato (lista e edição).
 * O jurídico entra aqui além dos perfis operacionais (AF-3649).
 */
export const canAccessContractTypes = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [ROLES.ADMIN, ROLES.OPERATOR, ROLES.LEGAL]);
};

/** Usuário que só tem o perfil jurídico, sem nenhum perfil operacional acumulado. */
export const isLegalOnly = (user: User | null | undefined): boolean => {
    return hasAnyRole(user, [ROLES.LEGAL]) && !canManageOperations(user);
};
